import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";

const API_URL = process.env.REACT_APP_BASE_URL;
const TOKEN = "a94c7ac0083ae190f0fe61143a7547799b5334c4";

const headers = {
  Authorization: `Token ${TOKEN}`,
  "Content-Type": "application/json",
};

// path('', SACCodeListView.as_view(), name='saccode-list'),
//     path('create/', SACCodeCreateView.as_view(), name='saccode-create'),
//     path('<int:pk>/get/', SACCodeRetrieveView.as_view(), name='saccode-retrieve'),
//     path('<int:pk>/update/', SACCodeUpdateView.as_view(), name='saccode-update'),
//     path('<int:pk>/delete/', SACCodeDeleteView.as_view(), name='saccode-delete'),
//     path('dropdown/', SACCodeDropdown.as_view(), name='saccode-dropdown'),
//     path('dropdown/<str:field_name>/', SacDropdownView.as_view(), name='gp-master-dynamic-dropdown'),

export const fetchCurrencies = createAsyncThunk(
  "currency/fetchCurrencies",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any[];
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      if (search) params.append("search", search.trim());
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      const filterPayload = FilterBuilder(filters ?? []);

      const body = {
        filters: filterPayload,
      };

      const response: any = await Axios.post(
        `${API_ENDPOINT?.MASTERS?.CURRENCY?.LIST}?${params.toString()}`,
        body,
      );
      return {
        data: response.data.data || response.data || [],
        rowCount: response.data.count || 0,
      };
    } catch (error: any) {
      throw new Error("Failed to fetch Currency");
    }
  },
);

export const fetchCurrencyById = createAsyncThunk(
  "currency/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        API_ENDPOINT.MASTERS.CURRENCY.DETAIL(id),
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Currency by ID",
      );
    }
  },
);

export const fetchCurrencyByDropDown = createAsyncThunk(
  "currency/fetchByDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(API_ENDPOINT.MASTERS.CURRENCY.DROPDOWN);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Currency list",
      );
    }
  },
);

export const updateCurrency = createAsyncThunk(
  "currency/updateCurrency",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.put(
        API_ENDPOINT.MASTERS.CURRENCY.UPDATE(data.id),
        data,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update Currency");
    }
  },
);

export const updateStatusInCurrency = createAsyncThunk(
  "currency/updateStatusInCurrency",
  async (data: any, { rejectWithValue }) => {
    try {
      const payload = { is_active: data.is_active };
      const response: any = await Axios.patch(
        API_ENDPOINT.MASTERS.CURRENCY.STATUS_UPDATE(data.id),
        payload,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update status in Currency",
      );
    }
  },
);

export const addCurrency = createAsyncThunk(
  "currency/addCurrency",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        API_ENDPOINT.MASTERS.CURRENCY.CREATE,
        data,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to add Currency");
    }
  },
);

export const deleteCurrency = createAsyncThunk(
  "currency/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      await Axios.delete(API_ENDPOINT.MASTERS.CURRENCY.DELETE(id));
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const exportCurrencyCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: {
    filterField: string;
    filterValue: any;
  }[];
}) => {
  const params = new URLSearchParams();

  params.append("export", "excel");

  if (search) params.append("search", search);

  // -----------------------------
  // Build structured filters body
  // -----------------------------
  const filterPayload = FilterBuilder(filters ?? []);

  const body = {
    filters: filterPayload,
  };

  const response = await Axios.post(
    `${API_ENDPOINT.MASTERS.CURRENCY.EXPORT}?${params.toString()}`,
    body,
    { responseType: "blob" },
  );

  return {
    blob: response.data,
    filename: `${
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "Currency List"
    }_${new Date().toISOString().split("T")[0]}.csv`,
  };
};
